import type { WordView } from '../../shared/results';
import { recapSlot, type RecapPlan, type RecapWord } from '../../game/recap';
import type { RevealStage } from './reveal-stages';
import { traceCompletion } from '../game/board-trace';
import { SCORE_STEP_POINTS } from '../game/score-tally.svelte';

interface ScoringPlayer {
  readonly words: readonly Readonly<WordView>[];
}

/** Word recap state: the words counted into running scores and the one being shown. */
export interface WordTally {
  readonly tallied: ReadonlySet<string>;
  /** The scored word being shown, counting into its scorers' totals. */
  readonly current: RecapWord | null;
  /** The board's longest word, closing the tally and kept at standings; it adds no points. */
  readonly longest: RecapWord | null;
  /** 0–1 through the shown word's time; 0 when no word is shown. */
  readonly progress: number;
}

/**
 * Steps through the scored words one at a time: each word's share follows its base points
 * (`recapSlot`), and it counts into scores when it appears. The board's longest word closes the
 * tally and stays, fully traced, at standings. Nothing counts before the tally or without motion,
 * and every word counts at standings.
 */
export function wordTally(
  plan: RecapPlan,
  stage: RevealStage,
  fraction: number,
  counting: boolean,
): WordTally {
  const all = new Set(plan.words.map((word) => word.w));
  if (stage === 'standings') return { tallied: all, current: null, longest: plan.longest, progress: 1 };
  if (stage === 'intro' || !counting)
    return { tallied: new Set(), current: null, longest: null, progress: 0 };
  const wordsShare = plan.wordsMs / plan.tallyMs;
  if (fraction >= wordsShare && plan.longest !== null) {
    return {
      tallied: all,
      current: null,
      longest: plan.longest,
      progress: (fraction - wordsShare) / (1 - wordsShare),
    };
  }
  const { index, progress } = recapSlot(plan.words, fraction / wordsShare);
  return {
    tallied: new Set(plan.words.slice(0, index + 1).map((word) => word.w)),
    current: plan.words[index]!,
    longest: null,
    progress,
  };
}

/**
 * A player's running score: the server-awarded points of their words tallied so far. The current
 * word's points climb in the in-game ten-point steps as its trace completes, so the count lasts
 * as long as the word's trace.
 */
export function talliedScore(player: ScoringPlayer, tally: WordTally): number {
  return player.words.reduce((sum, word) => {
    if (!tally.tallied.has(word.w)) return sum;
    if (word.w !== tally.current?.w) return sum + word.pts;
    const completion = traceCompletion(tally.progress);
    return sum + (completion === 1 ? word.pts : countedSteps(word.pts * completion));
  }, 0);
}

function countedSteps(points: number): number {
  return Math.floor(points / SCORE_STEP_POINTS) * SCORE_STEP_POINTS;
}
